import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { PublicNav } from "@/components/layout/PublicNav";
import { AgentCard } from "@/components/agent/AgentCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useMeta } from "@/lib/useMeta";
import { useTitle, NewsletterBox } from "@/components/growth";
import { SiteFooter } from "@/components/layout/SiteFooter";

const SORTS = [["popular", "Más populares"], ["rating", "Mejor valorados"], ["newest", "Más recientes"], ["price_asc", "Precio: menor a mayor"], ["price_desc", "Precio: mayor a menor"]];

export default function Marketplace() {
  const { data: meta } = useMeta();
  useTitle("Marketplace de agentes IA");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("popular");
  const [price, setPrice] = useState("all");
  const { data: agents = [], isLoading } = useQuery({
    queryKey: ["market", q, category, sort, price],
    queryFn: async () => (await api.get("/marketplace", { params: { q, category, sort, price } })).data,
  });
  const filtered = q || category || price !== "all";
  const featured = !filtered && sort === "popular" ? agents.slice(0, 3) : [];
  const rest = featured.length ? agents.slice(3) : agents;

  return (
    <div className="noise min-h-screen bg-[#050505] text-white">
      <PublicNav />
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 pb-10 pt-16">
          <p className="eyebrow reveal mb-4">Marketplace</p>
          <h1 className="reveal font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">Agentes para cada negocio.</h1>
          <p className="reveal mt-4 max-w-xl text-zinc-400">Prueba gratis antes de comprar. Acceso inmediato tras el pago.</p>
          <div className="mt-10 flex flex-col gap-3 lg:flex-row">
            <div className="flex flex-1 items-center gap-3 border border-white/10 bg-[#0d0d0d] px-4 focus-within:border-blue-500" style={{ transition: "border-color 200ms" }}>
              <MagnifyingGlass size={18} className="text-zinc-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, función o sector…" className="h-12 flex-1 bg-transparent text-sm focus:outline-none" data-testid="marketplace-search-input" />
              {q && <button onClick={() => setQ("")} data-testid="marketplace-clear-search"><X size={16} className="text-zinc-500" /></button>}
            </div>
            <Select value={price} onValueChange={setPrice}>
              <SelectTrigger className="h-12 w-full rounded-sm border-white/10 bg-[#0d0d0d] lg:w-44" data-testid="marketplace-price-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#121212] text-white">
                <SelectItem value="all">Todos los precios</SelectItem>
                <SelectItem value="free">Gratis</SelectItem>
                <SelectItem value="paid">De pago</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-12 w-full rounded-sm border-white/10 bg-[#0d0d0d] lg:w-56" data-testid="marketplace-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#121212] text-white">
                {SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {["", ...(meta?.categories || [])].map((c) => (
              <button
                key={c || "all"} onClick={() => setCategory(c)} data-testid={`marketplace-category-${c ? c.toLowerCase().replace(/\s+/g, "-") : "all"}`}
                className={`border px-3 py-1.5 text-xs font-medium ${category === c ? "border-blue-500 bg-blue-600 text-white" : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
                style={{ transition: "background-color 160ms, border-color 160ms, color 160ms" }}
              >
                {c || "Todas"}
              </button>
            ))}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 py-12">
        {isLoading ? (
          <div className="h-px w-full bg-white/10 thinking-beam" />
        ) : agents.length === 0 ? (
          <div className="border border-dashed border-white/15 p-16 text-center" data-testid="marketplace-empty">
            <p className="font-display text-2xl font-bold">Sin resultados</p>
            <p className="mt-2 text-sm text-zinc-500">Prueba con otra búsqueda o categoría.</p>
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <div className="mb-px grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3" data-testid="marketplace-featured">
                <div className="lg:col-span-2 lg:row-span-2"><AgentCard agent={featured[0]} large /></div>
                {featured.slice(1).map((a, i) => <AgentCard key={a.agent_id} agent={a} index={i + 1} />)}
              </div>
            )}
            <p className="mb-4 mt-10 text-xs uppercase tracking-widest text-zinc-500" data-testid="marketplace-count">{agents.length} agentes</p>
            <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="marketplace-grid">
              {rest.map((a, i) => <AgentCard key={a.agent_id} agent={a} index={i} />)}
            </div>
          </>
        )}
        <div className="mt-16"><NewsletterBox source="marketplace" /></div>
      </section>
      <SiteFooter />
    </div>
  );
}
